import { useEffect, useState } from 'preact/hooks';
import { signal } from '@preact/signals';
import { route } from 'preact-router';
import { api, ApiError } from '../api/client';
import { CATEGORIES } from '@elon-tools/shared';

import { AdminActionsPanel } from './AdminActions';
import { AdminSkills } from './AdminSkills';
import { SkeletonBar, Button, Input, Select, Textarea } from '../components/ui';

import { T } from '../styles/tokens';
import { tenantBranding } from '../stores/tenant.store';
type Tab = 'agents' | 'settings' | 'integrations' | 'customers' | 'stats' | 'actions' | 'skills';
const tab = signal<Tab>('agents');
const agents = signal<any[]>([]);
const settings = signal<Record<string, any>>({});
const integrations = signal<any[]>([]);
const customers = signal<any[]>([]);
const stats = signal<any>(null);
const tenantConfig = signal<any>(null);
const loading = signal(false);
const error = signal('');

// Agent creation
const showCreate = signal(false);
const newAgent = signal({ name: '', description: '', category_id: '', system_prompt: '' });
const creating = signal(false);

// Agent editing
const showEdit = signal(false);
const editAgent = signal<any>(null);
const editForm = signal({ name: '', description: '', category_id: '', system_prompt: '' });
const savingEdit = signal(false);

// Global safety rules
const showSafetyModal = signal(false);
const safetyRules = signal('');
const savingSafety = signal(false);

async function loadTab(t: Tab) {
  loading.value = true; error.value = '';
  try {
    if (t === 'agents') agents.value = (await api.get<any>('/admin/agents?limit=100')).agents;
    else if (t === 'settings') settings.value = (await api.get<any>('/admin/settings')).settings;
    else if (t === 'integrations') integrations.value = (await api.get<any>('/admin/integrations')).integrations;
    else if (t === 'customers') customers.value = (await api.get<any>('/admin/customers?limit=100')).customers;
    else if (t === 'stats') {
      stats.value = await api.get('/admin/stats');
      try { tenantConfig.value = (await api.get<any>('/tenant/config'))?.data; } catch {}
    }
  } catch (e) { error.value = (e as Error).message; }
  finally { loading.value = false; }
}

async function loadSafetyRules() {
  try {
    const s: any = await api.get('/admin/settings');
    safetyRules.value = String(s.settings?.global_safety_rules?.value ?? '');
  } catch { /* ignore */ }
}

async function saveSafetyRules() {
  savingSafety.value = true; error.value = '';
  try {
    await api.patch('/admin/settings', { global_safety_rules: safetyRules.value });
    showSafetyModal.value = false;
  } catch (e: any) { error.value = e.message; }
  finally { savingSafety.value = false; }
}

async function createAgent(e: Event) {
  e.preventDefault(); creating.value = true; error.value = '';
  try {
    await api.post('/admin/agents', { ...newAgent.value, templates: {}, params: { temperature: 0.7, max_tokens: 2048 }, input_schema: { type: 'object', properties: { prompt: { type: 'string', title: 'Prompt' } } } });
    showCreate.value = false;
    newAgent.value = { name: '', description: '', category_id: '', system_prompt: '' };
    await loadTab('agents');
  } catch (e: any) { error.value = e.details ? `${e.message}: ${JSON.stringify(e.details)}` : e.message; }
  finally { creating.value = false; }
}

async function openEditAgent(agent: any) {
  // Load full agent with config to get system_prompt
  try {
    const full: any = await api.get(`/admin/agents/${agent.id}`);
    const a = full.agent;
    editAgent.value = a;
    editForm.value = {
      name: a.name ?? '',
      description: a.description ?? '',
      category_id: a.category_id ?? '',
      system_prompt: a.config?.system_prompt ?? '',
    };
    showEdit.value = true;
  } catch (e: any) { error.value = e.message; }
}

async function saveEditAgent(e: Event) {
  e.preventDefault(); savingEdit.value = true; error.value = '';
  try {
    // Update meta (name, description, category)
    await api.patch(`/admin/agents/${editAgent.value.id}`, {
      name: editForm.value.name,
      description: editForm.value.description,
      category_id: editForm.value.category_id,
    });
    // Update config (system_prompt) — creates new version
    if (editForm.value.system_prompt !== (editAgent.value.config?.system_prompt ?? '')) {
      await api.post(`/admin/agents/${editAgent.value.id}/configs`, {
        system_prompt: editForm.value.system_prompt,
      });
    }
    showEdit.value = false;
    await loadTab('agents');
  } catch (e: any) { error.value = e.details ? `${e.message}: ${JSON.stringify(e.details)}` : e.message; }
  finally { savingEdit.value = false; }
}

async function deleteAgent() {
  if (!confirm(`Tem certeza que deseja desativar "${editAgent.value.name}"?`)) return;
  error.value = '';
  try {
    await api.delete(`/admin/agents/${editAgent.value.id}`);
    showEdit.value = false;
    await loadTab('agents');
  } catch (e: any) { error.value = e.message; }
}

// ══════════════════════════════════════════
// INTEGRATIONS PANEL
// ══════════════════════════════════════════

const PROVIDERS = [
 { id: 'workspace', name: 'Workspace', icon: '', fields: [
    { key: 'email', label: 'Email', type: 'email', placeholder: 'elon.parker@castelodigital.net' },
    { key: 'password', label: 'Senha', type: 'password', placeholder: '••••••••' },
  ]},
 { id: 'trello', name: 'Trello', icon: '', fields: [
    { key: 'api_key', label: 'API Key', type: 'text', placeholder: 'Trello API Key' },
    { key: 'token', label: 'Token', type: 'password', placeholder: 'Trello Token' },
  ]},
 { id: 'dynadot', name: 'Dynadot', icon: '', fields: [
    { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'Dynadot API Key' },
    { key: 'secret_key', label: 'Secret Key', type: 'password', placeholder: 'Dynadot Secret Key' },
  ]},
 { id: 'cloudflare', name: 'Cloudflare', icon: '', fields: [
    { key: 'api_token', label: 'API Token', type: 'password', placeholder: 'Cloudflare API Token' },
    { key: 'account_id', label: 'Account ID', type: 'text', placeholder: 'Account ID' },
  ]},
 { id: 'github', name: 'GitHub', icon: '', fields: [
    { key: 'token', label: 'Personal Access Token', type: 'password', placeholder: 'ghp_...' },
    { key: 'username', label: 'Username', type: 'text', placeholder: 'ElonParker' },
  ]},
 { id: 'resend', name: 'Resend', icon: '', fields: [
    { key: 'api_key', label: 'API Key', type: 'password', placeholder: 're_...' },
    { key: 'from_email', label: 'From Email', type: 'email', placeholder: 'noreply@yourdomain.com' },
  ]},
 { id: 'anthropic', name: 'Anthropic', icon: '', fields: [
    { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'sk-ant-...' },
  ]},
 { id: 'google', name: 'Google / Gmail', icon: '', fields: [
    { key: 'client_id', label: 'Client ID', type: 'text', placeholder: 'Google Client ID' },
    { key: 'client_secret', label: 'Client Secret', type: 'password', placeholder: 'GOCSPX-...' },
    { key: 'refresh_token', label: 'Refresh Token', type: 'password', placeholder: '1//0h...' },
  ]},
];

const savingProvider = signal<string | null>(null);
const integrationMsg = signal<{ type: 'ok' | 'err'; text: string } | null>(null);

function getProviderStatus(providerId: string): 'configured' | 'not_configured' {
  return integrations.value.some((i: any) => i.provider === providerId) ? 'configured' : 'not_configured';
}

async function saveIntegration(providerId: string, form: HTMLFormElement) {
  savingProvider.value = providerId;
  integrationMsg.value = null;
  const formData = new FormData(form);
  const credentials: Record<string, string> = {};
  for (const [k, v] of formData.entries()) {
    if (v && typeof v === 'string' && v.trim()) credentials[k] = v.trim();
  }
  if (Object.keys(credentials).length === 0) {
    integrationMsg.value = { type: 'err', text: 'Preencha pelo menos um campo' };
    savingProvider.value = null;
    return;
  }
  try {
    await api.post('/admin/integrations/save', { provider: providerId, credentials });
    integrationMsg.value = { type: 'ok', text: `${providerId} salvo com sucesso!` };
    await loadTab('integrations');
    form.reset();
  } catch (e) {
    integrationMsg.value = { type: 'err', text: (e as Error).message };
  } finally {
    savingProvider.value = null;
  }
}

const copyingKey = signal<string | null>(null);
const copiedKey = signal<string | null>(null);

async function copyIntegrationValue(provider: string, keyName: string) {
  const uid = `${provider}/${keyName}`;
  copyingKey.value = uid;
  try {
    const res = await api.get<any>(`/admin/integrations/${provider}/${keyName}/value`);
    await navigator.clipboard.writeText(res.value);
    copiedKey.value = uid;
    setTimeout(() => { copiedKey.value = null; }, 2000);
  } catch (e) {
    integrationMsg.value = { type: 'err', text: (e as Error).message };
  } finally {
    copyingKey.value = null;
  }
}

async function deleteIntegration(provider: string, keyName: string) {
  if (!confirm(`Remover ${provider}/${keyName}?`)) return;
  try {
    await api.delete(`/admin/integrations/${provider}/${keyName}`);
    await loadTab('integrations');
  } catch (e) {
    integrationMsg.value = { type: 'err', text: (e as Error).message };
  }
}

function IntegrationsPanel() {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
 <h2 style={{ margin: 0, fontSize: 18 }}>Gerenciar Integrações</h2>
        <Button variant="ghost" 
          class="btn btn-primary btn-sm"
          onClick={() => route('/docs')}
          style={{ fontSize: 13 }}
        >
 Documentação Completa
        </Button>
      </div>

      {integrationMsg.value && (
        <div class={integrationMsg.value.type === 'ok' ? 'success-banner' : 'error-banner'} style={{ marginBottom: 16 }}>
          {integrationMsg.value.text}
        </div>
      )}

      <div style={{ display: 'grid', gap: 16 }}>
        {PROVIDERS.map((p) => {
          const status = getProviderStatus(p.id);
          const providerKeys = integrations.value.filter((i: any) => i.provider === p.id);

          return (
            <div class="card" key={p.id} style={{ border: status === 'configured' ? '1px solid var(--success)' : `1px solid ${T.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 28 }}>{p.icon}</span>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 16 }}>{p.name}</h3>
                    <span class={`badge ${status === 'configured' ? 'badge-success' : 'badge-warning'}`} style={{ fontSize: 11 }}>
 {status === 'configured' ? 'Configurado' : 'Não configurado'}
                    </span>
                  </div>
                </div>
                <Button variant="ghost"
                  class="btn btn-sm"
                  onClick={() => route(`/docs`)}
                  title={`Ver documentação de ${p.name}`}
                  style={{
                    background: T.accent,
                    color: T.bg,
                    border: 'none',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    minWidth: '32px',
                    textAlign: 'center'
                  }}
                >
                  ?
                </Button>
              </div>

              {providerKeys.length > 0 && (
                <div style={{ marginBottom: 12, padding: '8px 12px', background: T.bgSecondary, borderRadius: 8, fontSize: 13 }}>
                  <strong style={{ fontSize: 11, color: T.textMuted }}>Chaves salvas:</strong>
                  {providerKeys.map((k: any) => {
                    const uid = `${k.provider}/${k.key_name}`;
                    return (
                      <div key={k.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, gap: 8 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
 <span style={{ fontWeight: 600 }}> {k.key_name}</span>
                          <span style={{ marginLeft: 8, fontFamily: T.fontMono, fontSize: 12, color: T.textMuted, letterSpacing: '0.5px' }}>
                            {k.preview || '••••••'}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                          <Button variant="ghost"
                            class="btn btn-sm"
                            style={{ background: copiedKey.value === uid ? T.success : T.bgSecondary, color: T.text, padding: '2px 10px', fontSize: 11, borderRadius: 6, border: 'none', cursor: 'pointer' }}
                            onClick={() => copyIntegrationValue(k.provider, k.key_name)}
                            disabled={copyingKey.value === uid}
                          >
 {copiedKey.value === uid ? 'Copiado!' : copyingKey.value === uid ? '' : 'Copiar'}
                          </Button>
                          <Button variant="ghost" class="btn btn-sm" style={{ color: T.danger, background: 'none', padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}
                            onClick={() => deleteIntegration(k.provider, k.key_name)}></Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <form onSubmit={(e) => { e.preventDefault(); saveIntegration(p.id, e.target as HTMLFormElement); }}>
                <div style={{ display: 'grid', gap: 8, gridTemplateColumns: p.fields.length > 2 ? '1fr' : '1fr 1fr' }}>
                  {p.fields.map((f) => (
                    <div key={f.key}>
                      <label style={{ fontSize: 12, color: T.textMuted, marginBottom: 4, display: 'block' }}>{f.label}</label>
                      <input class="input" name={f.key} type={f.type} placeholder={f.placeholder} style={{ fontSize: 13 }} />
                    </div>
                  ))}
                </div>
                <Button variant="ghost" class="btn btn-primary btn-sm" type="submit" style={{ marginTop: 12 }}
                  disabled={savingProvider.value === p.id}>
 {savingProvider.value === p.id ? 'Salvando...' : `Salvar ${p.name}`}
                </Button>
              </form>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function AdminPage(_props: { path?: string }) {
  useEffect(() => { loadTab(tab.value); }, [tab.value]);

  const tabs: { key: Tab; label: string }[] = [
 { key: 'stats', label: 'Stats' },
 { key: 'agents', label: 'Agentes' },
 { key: 'actions', label: 'Actions' },
 { key: 'skills', label: 'Skills' },
 { key: 'settings', label: 'Settings' },
 { key: 'integrations', label: 'Integrações' },
 { key: 'customers', label: 'Customers' },
  ];

  return (
    <div>
      <div class="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
 <h1>Dashboard{tenantBranding.value?.name ? ` — ${tenantBranding.value.name}` : ''}</h1>
        <Button
          variant="primary"
          onClick={() => route('/admin/notifications')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
 Notificações & Crescimento
        </Button>
      </div>
      
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {tabs.map((t) => (
          <Button variant="ghost" key={t.key} class={`btn btn-sm ${tab.value === t.key ? 'btn-primary' : 'btn-secondary'}`} onClick={() => (tab.value = t.key)}>
            {t.label}
          </Button>
        ))}
      </div>

      {error.value && <div class="error-banner">{error.value}</div>}
      {loading.value && (<div style={{ padding: 12 }}>
          <SkeletonBar width="60%" height={12} />
          <div style={{ height: 6 }} />
          <SkeletonBar width="40%" height={12} />
        </div>)}

      {!loading.value && tab.value === 'stats' && stats.value && (
        <div>
          {/* Cards do tenant (plano + limites) */}
          {tenantConfig.value && (
            <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
              <div class="card" style={{ minWidth: 140 }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#6C63FF' }}>{tenantConfig.value.plan || 'free'}</div>
                <div style={{ fontSize: 13, color: T.textMuted }}>Plano</div>
              </div>
              <div class="card" style={{ minWidth: 140 }}>
                <div style={{ fontSize: 22, fontWeight: 800 }}>{stats.value.total_customers ?? '?'}/{tenantConfig.value.max_customers ?? '∞'}</div>
                <div style={{ fontSize: 13, color: T.textMuted }}>Clientes / Limite</div>
              </div>
              <div class="card" style={{ minWidth: 140 }}>
                <div style={{ fontSize: 22, fontWeight: 800 }}>{stats.value.total_agents ?? '?'}/{tenantConfig.value.max_agents ?? '∞'}</div>
                <div style={{ fontSize: 13, color: T.textMuted }}>Agentes / Limite</div>
              </div>
            </div>
          )}
          <div class="card-grid">
            {Object.entries(stats.value).map(([k, v]) => (
              <div class="card" key={k}>
                <div style={{ fontSize: 28, fontWeight: 800 }}>{String(v)}</div>
                <div style={{ fontSize: 13, color: T.textMuted }}>{k.replace(/_/g, ' ')}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading.value && tab.value === 'agents' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <Button variant="primary" onClick={() => (showCreate.value = true)}>+ Criar Agente</Button>
            <Button variant="secondary" onClick={() => { loadSafetyRules(); showSafetyModal.value = true; }}> Regras Globais</Button>
          </div>
          <table class="table">
            <thead><tr><th>Nome</th><th>Categoria</th><th>Versão</th><th>Ativo</th><th></th></tr></thead>
            <tbody>
              {agents.value.filter((a: any) => a.is_active).map((a: any) => (
                <tr key={a.id}>
                  <td><strong>{a.name}</strong><br/><span style={{ fontSize: 12, color: T.textMuted }}>{a.description?.slice(0, 60)}</span></td>
                  <td>{CATEGORIES.find((c) => c.id === a.category_id)?.icon ?? ''} {CATEGORIES.find((c) => c.id === a.category_id)?.name ?? a.category_id}</td>
                  <td>v{a.current_config_version}</td>
                  <td>{a.is_active ? <span class="badge badge-success">Ativo</span> : <span class="badge badge-error">Inativo</span>}</td>
 <td><Button variant="secondary" size="xs" onClick={() => openEditAgent(a)}>Editar</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading.value && tab.value === 'actions' && (
        <AdminActionsPanel agents={agents.value} />
      )}

      {!loading.value && tab.value === 'settings' && (
        <div>
          {Object.entries(settings.value).map(([key, setting]: [string, any]) => {
            const isToken = key.includes('token');
            return (
              <div class="card" key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <strong>{key}</strong>
                  <div style={{ fontSize: 12, color: T.textMuted }}>{setting.description}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    class="input"
                    type={isToken ? 'password' : 'text'}
                    value={String(setting.value ?? '')}
                    style={{ width: isToken ? 280 : 200, fontFamily: T.fontMono, fontSize: 13 }}
                    onInput={(e) => {
                      const val = (e.target as HTMLInputElement).value;
                      settings.value = {
                        ...settings.value,
                        [key]: { ...setting, value: val === 'true' ? true : val === 'false' ? false : val },
                      };
                    }}
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    style={{ whiteSpace: 'nowrap' }}
                    onClick={async () => {
                      try {
                        await api.patch('/admin/settings', { [key]: settings.value[key].value });
                      } catch (e: any) { error.value = e.message; }
                    }}
                  >
                    Salvar
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading.value && tab.value === 'integrations' && <IntegrationsPanel />}

      {!loading.value && tab.value === 'skills' && <AdminSkills />}

      {!loading.value && tab.value === 'customers' && <CustomersTab />}

      {showCreate.value && (
        <div class="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) showCreate.value = false; }}>
          <div class="modal">
            <h2>Criar Agente</h2>
            <form onSubmit={createAgent}>
              <div class="form-group">
                <label class="label">Nome *</label>
                <input class="input" value={newAgent.value.name} required
                  onInput={(e) => { newAgent.value = { ...newAgent.value, name: (e.target as HTMLInputElement).value }; }} />
              </div>
              <div class="form-group">
                <label class="label">Descrição</label>
                <Textarea class="textarea" value={newAgent.value.description}
                  onInput={(e) => { newAgent.value = { ...newAgent.value, description: (e.target as HTMLTextAreaElement).value }; }} />
              </div>
              <div class="form-group">
                <label class="label">Categoria *</label>
                <select class="select" value={newAgent.value.category_id} required
                  onChange={(e) => { newAgent.value = { ...newAgent.value, category_id: (e.target as HTMLSelectElement).value }; }}>
                  <option value="">Selecione...</option>
                  {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                </select>
              </div>
              <div class="form-group">
                <label class="label">System Prompt *</label>
                <Textarea class="textarea" style={{ minHeight: 150 }} value={newAgent.value.system_prompt} required
                  onInput={(e) => { newAgent.value = { ...newAgent.value, system_prompt: (e.target as HTMLTextAreaElement).value }; }} />
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button variant="secondary" type="button" onClick={() => (showCreate.value = false)}>Cancelar</Button>
                <Button variant="primary" type="submit" disabled={creating.value} loading={creating.value}>{creating.value ? 'Criando...' : 'Criar'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEdit.value && editAgent.value && (
        <div class="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) showEdit.value = false; }}>
          <div class="modal">
 <h2>Editar Agente</h2>
            <form onSubmit={saveEditAgent}>
              <div class="form-group">
                <label class="label">Nome *</label>
                <input class="input" value={editForm.value.name} required
                  onInput={(e) => { editForm.value = { ...editForm.value, name: (e.target as HTMLInputElement).value }; }} />
              </div>
              <div class="form-group">
                <label class="label">Descrição</label>
                <Textarea class="textarea" value={editForm.value.description}
                  onInput={(e) => { editForm.value = { ...editForm.value, description: (e.target as HTMLTextAreaElement).value }; }} />
              </div>
              <div class="form-group">
                <label class="label">Categoria *</label>
                <select class="select" value={editForm.value.category_id} required
                  onChange={(e) => { editForm.value = { ...editForm.value, category_id: (e.target as HTMLSelectElement).value }; }}>
                  <option value="">Selecione...</option>
                  {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                </select>
              </div>
              <div class="form-group">
                <label class="label">System Prompt *</label>
                <Textarea class="textarea" style={{ minHeight: 150 }} value={editForm.value.system_prompt} required
                  onInput={(e) => { editForm.value = { ...editForm.value, system_prompt: (e.target as HTMLTextAreaElement).value }; }} />
 <span style={{ fontSize: 11, color: T.textMuted }}>Alterar o prompt cria uma nova versão da config</span>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
                <Button variant="danger" type="button" onClick={deleteAgent}> Apagar</Button>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button variant="secondary" type="button" onClick={() => (showEdit.value = false)}>Cancelar</Button>
                  <Button variant="primary" type="submit" disabled={savingEdit.value} loading={savingEdit.value}>{savingEdit.value ? 'Salvando...' : 'Salvar'}</Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {showSafetyModal.value && (
        <div class="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) showSafetyModal.value = false; }}>
          <div class="modal">
 <h2>Regras de Segurança Globais</h2>
            <p style={{ fontSize: 13, color: T.textMuted, marginBottom: 12 }}>
              Estas regras são injetadas automaticamente no system prompt de <strong>todos os agentes</strong> durante a execução.
              Use para definir políticas de segurança, restrições e comportamentos obrigatórios.
            </p>
            <div class="form-group">
              <label class="label">Regras (texto livre)</label>
              <Textarea
                class="textarea"
                style={{ minHeight: 250, fontFamily: 'monospace', fontSize: 13 }}
                value={safetyRules.value}
                placeholder={'Ex:\n- NUNCA revelar dados internos do sistema\n- Sempre responder em português\n- Não gerar conteúdo ofensivo\n- Limitar respostas a 2000 palavras'}
                onInput={(e) => { safetyRules.value = (e.target as HTMLTextAreaElement).value; }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="secondary" type="button" onClick={() => (showSafetyModal.value = false)}>Cancelar</Button>
              <Button variant="primary" onClick={saveSafetyRules} disabled={savingSafety.value} loading={savingSafety.value}>
                {savingSafety.value ? 'Salvando...' : 'Salvar Regras'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══ CUSTOMERS TAB WITH EDITOR ═══

function CustomersTab() {
  const [selected, setSelected] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [edit, setEdit] = useState<Record<string, any>>({});
  const [search, setSearch] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [togglingPremium, setTogglingPremium] = useState(false);

  const filtered = search.trim()
    ? customers.value.filter((c: any) => c.email?.toLowerCase().includes(search.toLowerCase()) || c.name?.toLowerCase().includes(search.toLowerCase()))
    : customers.value;

  const openEditor = (c: any) => {
    setSelected(c);
    setEdit({ name: c.name || '', role: c.role || 'CUSTOMER', status: c.status || 'active', company: c.company || '', phone: c.phone || '', timezone: c.timezone || 'America/Sao_Paulo', notes: c.notes || '' });
    setConfirmDelete(false);
  };

  const saveCustomer = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.patch(`/cloud/admin/customers/${selected.id}`, edit);
      // Update local state
      customers.value = customers.value.map((c: any) => c.id === selected.id ? { ...c, ...edit } : c);
      setSelected({ ...selected, ...edit });
    } catch {}
    setSaving(false);
  };

  const deleteCustomer = async () => {
    if (!selected) return;
    try {
      await api.delete(`/cloud/admin/customers/${selected.id}`);
      customers.value = customers.value.filter((c: any) => c.id !== selected.id);
      setSelected(null);
    } catch {}
  };

  const togglePremium = async () => {
    if (!selected) return;
    setTogglingPremium(true);
    try {
      const isPremium = !!selected.subscription;
      await api.post(`/cloud/admin/customers/${selected.id}/toggle-premium`, { premium: !isPremium });
      // Refresh
      const res = await api.get<any>(`/cloud/admin/customers/${selected.id}`);
      const updated = res.customer;
      customers.value = customers.value.map((c: any) => c.id === selected.id ? { ...c, subscription: updated.subscription } : c);
      setSelected({ ...selected, subscription: updated.subscription });
    } catch {}
    setTogglingPremium(false);
  };

  const C = { bg: T.bg, card: T.bgInput, border: T.border, text: T.text, dim: T.textMuted, mid: T.textSecondary, accent: T.primary };

  return (
    <div style={{ display: 'flex', gap: 16, minHeight: 500 }}>
      {/* Left: Customer list */}
      <div style={{ flex: '0 0 380px', maxWidth: 380 }}>
        <Input placeholder="Buscar por email ou nome..." value={search}
          onChange={(v) => setSearch(v)}
          style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.border}`, background: T.bgCard, color: C.text, fontSize: 13, marginBottom: 12, outline: 'none', boxSizing: 'border-box' }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 520, overflowY: 'auto' }}>
          {filtered.map((c: any) => {
            const isActive = selected?.id === c.id;
            const hasSub = !!c.subscription;
            return (
              <div key={c.id} onClick={() => openEditor(c)} style={{
                padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
                background: isActive ? T.primaryBg : T.bgSubtle,
                border: isActive ? `1px solid T.primaryBg` : `1px solid ${C.border}`,
                transition: 'all 0.12s',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: C.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.email}</span>
                  {c.google_id ? (
                    <span title="Cadastro via Google" style={{ padding: '1px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700, background: 'rgba(66,133,244,0.1)', color: T.primary, display: 'flex', alignItems: 'center', gap: 3 }}>
                      <svg width="10" height="10" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A11.96 11.96 0 001 12c0 1.94.46 3.77 1.18 5.43l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                      Google
                    </span>
                  ) : (
                    <span title="Cadastro via Email" style={{ padding: '1px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700, background: T.bgHover, color: T.textSecondary, display: 'flex', alignItems: 'center', gap: 3 }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 7l-10 7L2 7"/></svg>
                      Email
                    </span>
                  )}
                  {hasSub && <span style={{ padding: '1px 6px', borderRadius: 4, fontSize: 9, fontWeight: 800, background: T.primaryBg, color: C.accent }}>PRO</span>}
                  <span style={{ padding: '1px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700, background: c.status === 'active' ? T.successBg : T.dangerBg, color: c.status === 'active' ? T.success : T.danger }}>{c.status}</span>
                </div>
                <div style={{ fontSize: 10, color: C.dim, marginTop: 3 }}>
                  {c.role} · {c.machine_count || 0} máquina{(c.machine_count || 0) !== 1 ? 's' : ''} · {c.created_at ? new Date(c.created_at).toLocaleDateString('pt-BR') : ''}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ fontSize: 11, color: C.dim, marginTop: 8, textAlign: 'center' }}>{filtered.length} de {customers.value.length} customers</div>
      </div>

      {/* Right: Editor */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {!selected ? (
          <div style={{ textAlign: 'center', padding: 60, color: C.dim, fontSize: 13 }}>Selecione um customer para editar</div>
        ) : (
          <div style={{ padding: '20px 24px', borderRadius: 14, background: C.card, border: `1px solid ${C.border}` }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, paddingBottom: 16, borderBottom: `1px solid ${C.border}` }}>
              <div style={{
                width: 44, height: 44, borderRadius: 12,
                background: `linear-gradient(135deg, hsl(${selected.email.charCodeAt(0) % 360}, 40%, 35%), hsl(${(selected.email.charCodeAt(0) + 40) % 360}, 40%, 30%))`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18, fontWeight: 800, color: T.text,
              }}>{(selected.name || selected.email).charAt(0).toUpperCase()}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>{selected.name || selected.email}</div>
                <div style={{ fontSize: 12, color: C.dim }}>{selected.email}</div>
              </div>
              <Button variant="ghost" size="xs" onClick={() => setSelected(null)} style={{ color: C.dim, fontSize: 18 }}>×</Button>
            </div>

            {/* Info cards */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
              <InfoBox label="ID" value={selected.id?.slice(0, 12) + '...'} />
              <InfoBox label="Role" value={selected.role} color={selected.role === 'ADMIN' ? T.warning : C.mid} />
              <InfoBox label="Status" value={selected.status} color={selected.status === 'active' ? T.success : T.danger} />
              <InfoBox label="Premium" value={selected.subscription ? `${selected.subscription.plan === 'premium_yearly' ? 'Anual' : 'Mensal'}` : 'Não'} color={selected.subscription ? C.accent : C.dim} />
              <InfoBox label="Máquinas" value={String(selected.machine_count || 0)} />
              <InfoBox label="Criado em" value={selected.created_at ? new Date(selected.created_at).toLocaleDateString('pt-BR') : '—'} />
              <InfoBox label="IP de Cadastro" value={selected.signup_ip || '—'} />
              <InfoBox label="País" value={selected.signup_country || '—'} />
              <InfoBox label="Último Login" value={selected.last_login_at ? new Date(selected.last_login_at).toLocaleDateString('pt-BR') + ' ' + new Date(selected.last_login_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '—'} />
              <InfoBox label="IP Último Login" value={selected.last_login_ip || '—'} />
            </div>

            {/* Edit fields */}
            <div style={{ display: 'grid', gap: 12, marginBottom: 20 }}>
              <Field label="Nome" value={edit.name} onChange={(v) => setEdit({ ...edit, name: v })} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <SelectField label="Role" value={edit.role} options={[{ v: 'CUSTOMER', l: 'Customer' }, { v: 'ADMIN', l: 'Admin' }]} onChange={(v) => setEdit({ ...edit, role: v })} />
                <SelectField label="Status" value={edit.status} options={[{ v: 'active', l: 'Ativo' }, { v: 'inactive', l: 'Inativo' }, { v: 'suspended', l: 'Suspenso' }, { v: 'deleted', l: 'Deletado' }]} onChange={(v) => setEdit({ ...edit, status: v })} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <Field label="Empresa" value={edit.company} onChange={(v) => setEdit({ ...edit, company: v })} />
                <Field label="Telefone" value={edit.phone} onChange={(v) => setEdit({ ...edit, phone: v })} />
              </div>
              <Field label="Notas internas" value={edit.notes} onChange={(v) => setEdit({ ...edit, notes: v })} multiline />
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button variant="primary" size="sm" onClick={saveCustomer} disabled={saving} loading={saving}>{saving ? 'Salvando...' : 'Salvar'}</Button>

              <Button variant={selected.subscription ? 'danger' : 'success'} size="sm" onClick={togglePremium} disabled={togglingPremium} style={{ background: selected.subscription ? T.dangerBg : T.successBg }}>
                {togglingPremium ? '...' : selected.subscription ? 'Remover Premium' : 'Dar Premium'}
              </Button>

              {!confirmDelete ? (
                <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(true)} style={{ color: T.danger, border: `1px solid ${T.dangerBg}`, marginLeft: 'auto' }}>Deletar</Button>
              ) : (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginLeft: 'auto' }}>
                  <span style={{ fontSize: 11, color: T.danger, fontWeight: 600 }}>Tem certeza?</span>
                  <Button variant="danger" size="xs" onClick={deleteCustomer}>Sim, deletar</Button>
                  <Button variant="ghost" size="xs" onClick={() => setConfirmDelete(false)}>Cancelar</Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoBox({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ padding: '8px 12px', borderRadius: 8, background: T.bgSubtle, border: `1px solid ${T.bgCard}` }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: color || T.text, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</div>
    </div>
  );
}

function Field({ label, value, onChange, multiline }: { label: string; value: string; onChange: (v: string) => void; multiline?: boolean }) {
  const style = {
    width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${T.bgHover}`,
    background: T.bgCard, color: T.text, fontSize: 12, outline: 'none', boxSizing: 'border-box' as const,
    fontFamily: 'inherit', resize: 'none' as const,
  };
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{label}</div>
      {multiline ? (
        <Textarea value={value} onInput={(e) => onChange((e.target as HTMLTextAreaElement).value)} rows={3} style={style} />
      ) : (
        <Input value={value} onChange={onChange} style={style} />
      )}
    </div>
  );
}

function SelectField({ label, value, options, onChange }: { label: string; value: string; options: { v: string; l: string }[]; onChange: (v: string) => void }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{label}</div>
      <Select value={value} onChange={onChange} options={options.map(o => ({ value: o.v, label: o.l }))} style={{
        width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${T.bgHover}`,
        background: T.bgCard, color: T.text, fontSize: 12, outline: 'none',
      }} />
    </div>
  );
}
